import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ChevronRight, Inbox } from "lucide-react";

const STATUS_LABEL = {
  open: { label: "Ouverte", cls: "bg-[#ECFBEF] text-[#0A7F2E]" },
  pending: { label: "En attente", cls: "bg-brand/10 text-brand" },
  closed: { label: "Clôturée", cls: "bg-surface text-subtle" },
};

export default function AdminSupport() {
  const { user, role } = useAuth();
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();

  useEffect(() => {
    if (role !== "client" || !user?.isAdmin) {
      nav("/");
      return;
    }
    (async () => {
      try {
        const params = filter !== "all" ? { status: filter } : {};
        const { data } = await api.get("/support/tickets", { params });
        setTickets(data.items || []);
      } catch {
        setTickets([]);
      }
    })();
  }, [filter, role, user, nav]);

  if (tickets === null) return <div className="container mx-auto py-16 text-center text-subtle">Chargement…</div>;

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 md:py-14 max-w-6xl">
      <p className="overline mb-2">Admin · Support</p>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-ink">Demandes de support</h1>
      <p className="mt-2 text-subtle">Toutes les demandes de tous les utilisateurs. {tickets.length} demande{tickets.length > 1 ? "s" : ""} affichée{tickets.length > 1 ? "s" : ""}.</p>

      <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="filter-bar">
        {[
          { v: "all", l: "Toutes" },
          { v: "open", l: "Ouvertes" },
          { v: "pending", l: "En attente" },
          { v: "closed", l: "Clôturées" },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`text-sm font-medium px-4 h-10 border-2 transition-colors ${filter === f.v ? "border-ink bg-ink text-white" : "border-border text-subtle hover:border-ink"}`}
            data-testid={`filter-${f.v}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tickets.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center">
            <Inbox className="mx-auto text-subtle" size={36} />
            <p className="mt-4 text-ink font-medium">Aucune demande</p>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border">
            {tickets.map((t) => {
              const st = STATUS_LABEL[t.status] || STATUS_LABEL.open;
              return (
                <Link
                  to={`/admin/support/${t.id}`}
                  key={t.id}
                  className="flex items-center gap-4 p-5 hover:bg-surface transition-colors"
                  data-testid={`admin-ticket-row-${t.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-xs text-subtle">{t.trackingNumber}</span>
                      <span className={`inline-block px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${st.cls}`}>
                        {st.label}
                      </span>
                      <span className="text-xs text-subtle">· {t.userType === "client" ? "Visiteur" : "Établissement"}</span>
                    </div>
                    <p className="font-display text-lg font-bold text-ink mt-1 truncate">{t.subject}</p>
                    <p className="text-xs text-subtle mt-1 truncate">
                      {t.userName} · {t.email} · {new Date(t.createdAt).toLocaleString("fr-FR")} · {(t.replies || []).length} réponse{(t.replies || []).length > 1 ? "s" : ""}
                    </p>
                  </div>
                  <ChevronRight className="text-subtle" size={18} />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
